/**
 * Utility functions for printing forms in Philippine Army style
 * Designed to exactly match the official PA Reservist Information Data Sheet format
 */

/**
 * Handles printing forms in Philippine Army Reservist Information Data Sheet style
 */
export const handlePrint = () => {
  try {
    // Create a new print window
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      console.error('Failed to open print window. Please check if pop-ups are blocked.');
      return;
    }

    // Get the form element (either ROTC or CWTS)
    const rotcForm = document.querySelector('.rotc-form');
    const cwtsForm = document.querySelector('.cwts-form');
    const form = rotcForm || cwtsForm;
    
    if (!form) {
      console.error('Form not found');
      return;
    }

    const formType = rotcForm ? 'ROTC' : 'CWTS';
    const formTitle = rotcForm ? 'ROTC Form' : 'CWTS Form';

    // Create the print document
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${formTitle}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 12.7mm; /* 0.5 inches */
            }
            body {
              margin: 0;
              padding: 0;
              font-family: Arial, sans-serif;
              font-size: 10pt;
              line-height: 1.2;
            }
            .rotc-form, .cwts-form {
              width: 210mm;
              min-height: 297mm;
              padding: 12.7mm;
              background: white;
              color: black;
            }
            .rotc-form-header, .cwts-form-header {
              text-align: left;
              margin-bottom: 15px;
            }
            .form-note {
              font-size: 8pt;
              margin-bottom: 5px;
              color: #333;
              text-align: left;
              font-style: italic;
            }
            .form-note a {
              color: blue;
              text-decoration: underline;
            }
            .rotc-form-header h2, .cwts-form-header h2,
            .rotc-form-header h3, .cwts-form-header h3 {
              font-size: 14pt;
              font-weight: bold;
              margin: 0;
              text-transform: uppercase;
              text-align: left;
            }
            .rotc-form-header h3, .cwts-form-header h3 {
              font-size: 12pt;
              margin: 5px 0;
            }
            .form-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 10px;
              page-break-inside: avoid;
            }
            .form-table th,
            .form-table td {
              border: 1px solid black;
              padding: 4px;
              text-align: left;
              vertical-align: top;
            }
            .section-header {
              background-color: #f0f0f0 !important;
              font-weight: bold;
              text-align: center;
              text-transform: uppercase;
              font-size: 10pt;
              padding: 4px;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .sub-header th {
              background-color: #f0f0f0 !important;
              font-weight: bold;
              text-align: center;
              padding: 4px;
              font-size: 9pt;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .form-label {
              font-weight: bold;
              font-size: 9pt;
              margin-bottom: 2px;
            }
            .form-value {
              min-height: 16px;
              font-size: 10pt;
            }
            * {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .certification {
              margin-top: 30px;
              font-size: 14px;
            }
            .signature-line {
              margin-top: 30px;
              display: flex;
              justify-content: space-between;
            }
            .signature-placeholder, 
            .date-placeholder {
              border-top: 1px solid #333;
              padding-top: 5px;
              width: 45%;
              text-align: center;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          ${form.outerHTML}
        </body>
      </html>
    `);

    // Wait for content to load then print
    printWindow.document.close();
    printWindow.focus();
    
    // Use a timeout to ensure styles are applied
    setTimeout(() => {
      printWindow.print();
      // Close the window after printing (optional)
      printWindow.close();
    }, 250);

  } catch (error) {
    console.error('Error during printing:', error);
    // Fallback to default print
    window.print();
  }
};

/**
 * Collects form data from the input form
 */
const collectFormData = (form) => {
  const data = {};
  
  // Process all input elements
  const inputs = form.querySelectorAll('input, select, textarea');
  inputs.forEach(input => {
    const name = input.name || input.id;
    if (name) {
      // Get value based on input type
      let value = '';
      if (input.type === 'checkbox' || input.type === 'radio') {
        value = input.checked ? (input.value || 'Yes') : '';
      } else {
        value = input.value || '';
      }
      
      // Store in data object
      if (name && value) {
        data[name] = value;
      }
    }
  });
  
  // Look for labeled data in table cells
  const labelCells = form.querySelectorAll('td[data-field-label]');
  labelCells.forEach(cell => {
    const label = cell.getAttribute('data-field-label');
    const valueCell = cell.nextElementSibling;
    if (label && valueCell) {
      const value = valueCell.textContent.trim();
      if (value) {
        data[label] = value;
      }
    }
  });
  
  // Also look for any elements with data-field attributes
  const dataFields = form.querySelectorAll('[data-field]');
  dataFields.forEach(field => {
    const name = field.getAttribute('data-field');
    if (name) {
      data[name] = field.textContent.trim() || field.value || '';
    }
  });
  
  return data;
};

/**
 * Creates a standardized form matching the Philippine Army template
 */
const createStandardForm = (formData, formType) => {
  const formContent = document.createElement('div');
  formContent.className = 'pa-form-content';
  
  // Build form HTML sections with the exact structure as seen in the image
  formContent.innerHTML = `
    <!-- RESERVIST PERSONAL INFORMATION -->
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10pt; border: 1px solid black;">
      <tr>
        <th colspan="4" style="background-color: #e6e6e6; padding: 5px; text-align: center; text-transform: uppercase; font-weight: bold; border: 1px solid black;">
          RESERVIST PERSONAL INFORMATION
        </th>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px; width: 25%;">
          <div style="font-weight: bold; font-size: 9pt;">Last Name</div>
          <div>${formData.lastName || ''}</div>
        </td>
        <td style="border: 1px solid black; padding: 5px; width: 25%;">
          <div style="font-weight: bold; font-size: 9pt;">First Name</div>
          <div>${formData.firstName || ''}</div>
        </td>
        <td style="border: 1px solid black; padding: 5px; width: 25%;">
          <div style="font-weight: bold; font-size: 9pt;">Middle Name</div>
          <div>${formData.middleName || ''}</div>
        </td>
        <td style="border: 1px solid black; padding: 5px; width: 25%;">
          <div style="font-weight: bold; font-size: 9pt;">Name Extension</div>
          <div>${formData.nameExt || ''}</div>
        </td>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Birthdate</div>
          <div>${formData.birthdate || ''}</div>
        </td>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Place of Birth</div>
          <div>${formData.birthplace || ''}</div>
        </td>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Citizenship</div>
          <div>${formData.citizenship || 'Filipino'}</div>
        </td>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Civil Status</div>
          <div>${formData.civilStatus || ''}</div>
        </td>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Religion</div>
          <div>${formData.religion || ''}</div>
        </td>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Blood Type</div>
          <div>${formData.bloodType || ''}</div>
        </td>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Sex</div>
          <div>${formData.sex || ''}</div>
        </td>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Contact Number</div>
          <div>${formData.phone || ''}</div>
        </td>
      </tr>
      <tr>
        <td colspan="4" style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Present Address</div>
          <div>${formData.address || ''}</div>
        </td>
      </tr>
      <tr>
        <td colspan="4" style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Permanent Address (If different from Present Address)</div>
          <div>&nbsp;</div>
        </td>
      </tr>
    </table>
    
    <!-- PERSONAL IDENTIFICATION -->
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10pt; border: 1px solid black;">
      <tr>
        <th colspan="4" style="background-color: #e6e6e6; padding: 5px; text-align: center; text-transform: uppercase; font-weight: bold; border: 1px solid black;">
          PERSONAL IDENTIFICATION
        </th>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px; width: 25%;">
          <div style="font-weight: bold; font-size: 9pt;">Height (cm)</div>
          <div>&nbsp;</div>
        </td>
        <td style="border: 1px solid black; padding: 5px; width: 25%;">
          <div style="font-weight: bold; font-size: 9pt;">Weight (kg)</div>
          <div>&nbsp;</div>
        </td>
        <td style="border: 1px solid black; padding: 5px; width: 25%;">
          <div style="font-weight: bold; font-size: 9pt;">Identifying Marks</div>
          <div>&nbsp;</div>
        </td>
        <td style="border: 1px solid black; padding: 5px; width: 25%;">
          <div style="font-weight: bold; font-size: 9pt;">TIN No.</div>
          <div>&nbsp;</div>
        </td>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">SSS/GSIS No.</div>
          <div>&nbsp;</div>
        </td>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">PhilHealth No.</div>
          <div>&nbsp;</div>
        </td>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Pag-IBIG No.</div>
          <div>&nbsp;</div>
        </td>
        <td style="border: 1px solid black; padding: 5px;">
          <div style="font-weight: bold; font-size: 9pt;">Student ID</div>
          <div>${formData.studentId || ''}</div>
        </td>
      </tr>
    </table>
    
    <!-- VACCINATION RECORDS -->
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10pt; border: 1px solid black;">
      <tr>
        <th colspan="5" style="background-color: #e6e6e6; padding: 5px; text-align: center; text-transform: uppercase; font-weight: bold; border: 1px solid black;">
          VACCINATION RECORDS
        </th>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 20%;">Vacci Name</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 20%;">Date of Vaccination</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 20%;">Vaccine Type/Brand</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 20%;">Batch Manufacturer</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 20%;">Country of Origin</td>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
      </tr>
    </table>
    
    <!-- AFPSAT QUALIFICATION -->
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10pt; border: 1px solid black;">
      <tr>
        <th colspan="2" style="background-color: #e6e6e6; padding: 5px; text-align: center; text-transform: uppercase; font-weight: bold; border: 1px solid black;">
          AFPSAT QUALIFICATION
        </th>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 50%;">Date of Exam</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 50%;">Rating</td>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
      </tr>
    </table>
    
    <!-- MILITARY SCHOOLS/TRAINING -->
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10pt; border: 1px solid black;">
      <tr>
        <th colspan="3" style="background-color: #e6e6e6; padding: 5px; text-align: center; text-transform: uppercase; font-weight: bold; border: 1px solid black;">
          MILITARY SCHOOLS/TRAINING
        </th>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 40%;">Course/Schooling</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 30%;">School</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 30%;">Date Completed</td>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
      </tr>
    </table>
    
    <!-- AWARDS AND DECORATION -->
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10pt; border: 1px solid black;">
      <tr>
        <th colspan="3" style="background-color: #e6e6e6; padding: 5px; text-align: center; text-transform: uppercase; font-weight: bold; border: 1px solid black;">
          AWARDS AND DECORATION
        </th>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 40%;">Award/Citation</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 30%;">Authority</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 30%;">Date Awarded</td>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
      </tr>
    </table>
    
    <!-- DEPENDENTS -->
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10pt; border: 1px solid black;">
      <tr>
        <th colspan="5" style="background-color: #e6e6e6; padding: 5px; text-align: center; text-transform: uppercase; font-weight: bold; border: 1px solid black;">
          DEPENDENTS
        </th>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 30%;">Name</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 20%;">Relation</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 25%;">Address</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 15%;">Contact No.</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 10%;">Date</td>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px;">${formData.emergencyContact || 'Aia Noel P Arguelles'}</td>
        <td style="border: 1px solid black; padding: 5px;">Emergency Contact</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">${formData.emergencyContactNo || '09501597626'}</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
      </tr>
    </table>
    
    <!-- EDUCATIONAL BACKGROUND -->
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10pt; border: 1px solid black;">
      <tr>
        <th colspan="3" style="background-color: #e6e6e6; padding: 5px; text-align: center; text-transform: uppercase; font-weight: bold; border: 1px solid black;">
          EDUCATIONAL BACKGROUND (TERTIARY LEVEL)
        </th>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 40%;">School</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 40%;">Course</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 20%;">Date Graduated</td>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">${formData.course || 'BSBA'}</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
      </tr>
    </table>
    
    <!-- UNIT ASSIGNMENT -->
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10pt; border: 1px solid black;">
      <tr>
        <th colspan="4" style="background-color: #e6e6e6; padding: 5px; text-align: center; text-transform: uppercase; font-weight: bold; border: 1px solid black;">
          UNIT ASSIGNMENT
        </th>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 25%;">Unit</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 25%;">Address</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 25%;">Date Start</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 25%;">Date End</td>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
      </tr>
    </table>
    
    <!-- MILITARY ACTIVITIES -->
    <table style="width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 10pt; border: 1px solid black;">
      <tr>
        <th colspan="3" style="background-color: #e6e6e6; padding: 5px; text-align: center; text-transform: uppercase; font-weight: bold; border: 1px solid black;">
          MILITARY ACTIVITIES
        </th>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 40%;">Activity</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 30%;">Date Start</td>
        <td style="border: 1px solid black; padding: 5px; text-align: center; font-weight: bold; width: 30%;">Date End</td>
      </tr>
      <tr>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
        <td style="border: 1px solid black; padding: 5px;">&nbsp;</td>
      </tr>
    </table>
  `;
  
  return formContent;
};